<script setup>
import { ref, computed, watch } from 'vue'
import { API_BASE_URL } from '../config'

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  editorCode: { type: String, default: null },
})

const emit = defineEmits(['update:modelValue'])

const isOpen = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value),
})
const checked = ref(false)
const category = ref('bug')
const title = ref('')
const message = ref('')
const errors = ref({
  title: '',
  category: '',
})

const options = ref([
  { text: 'Bug', value: 'bug' },
  { text: 'Feature', value: 'feature' },
  { text: 'Request', value: 'request' },
  { text: 'Sonstiges', value: 'feedback' },
])

const isSubmitting = ref(false)
const showSuccessDialog = ref(false)
const showErrorDialog = ref(false)

watch(category, (newVal) => {
  if (newVal === 'bug') {
    checked.value = true
  }
}, { immediate: true })

function closeAll() {
  showSuccessDialog.value = false
  isOpen.value = false
}

function validate() {
  errors.value = { title: '', category: '' }

  if (!title.value) errors.value.title = 'Titel fehlt'
  if (!category.value) errors.value.category = 'Bitte Kategorie wählen'

  return !errors.value.title && !errors.value.category
}

async function submit() {
  if (!validate()) return
  
  isSubmitting.value = true

  const formData = {
    title: title.value,
    category: category.value,
    message: message.value,
    code: checked.value ? props.editorCode : null,
    browserInfo: getBrowserInfo(),
  }
  try {
    const response = await fetch(`${API_BASE_URL}/api/feedback/`, {
      method: 'POST',
      redirect: 'follow',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(formData),
    })

    if (!response.ok) {
      showErrorDialog.value = true
      return
    }

    // Success!
    showSuccessDialog.value = true
    
    // Reset form after successful submission
    title.value = ''
    message.value = ''
    checked.value = false
    category.value = 'bug'
  } catch (error) {
    console.error('error:', error)
    showErrorDialog.value = true
  } finally {
    isSubmitting.value = false
  }
}


function getBrowserInfo() {
  return {
    userAgent: navigator.userAgent,
    language: navigator.language,
    platform: navigator.platform,
    screenWidth: window.screen.width,
    screenHeight: window.screen.height,
    windowWidth: window.innerWidth,
    windowHeight: window.innerHeight,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    online: navigator.onLine,
  }
}
</script>

<template>
  <Teleport to="body">
    <Transition name="fade">
      <div
        v-if="isOpen"
        class="fixed inset-0 z-9999 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
        @click.self="isOpen = false"
      >
        <div
          class="w-full max-w-md bg-secondary border border-tertiary p-8 rounded-outer-popup shadow-2xl text-left transform transition-all duration-300 scale-100 relative"
        >
          <!-- Close Button -->
          <button
            class="absolute top-5 right-5 text-text-main/60 hover:text-text-main text-2xl font-bold cursor-pointer"
            aria-label="Schließen"
            @click="isOpen = false"
          >
            &times;
          </button>

          <h3
            class="text-xl font-bold text-text-main flex items-center gap-2 border-b border-tertiary pb-3 mb-5"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="currentColor"
              class="text-accent"
            >
              <path
                d="M12,3.5C6.5,3.5 2,6.5 2,10.14C2,12.19 3.5,14 6,15.14C5.5,17 4.14,18.5 2,19C4.5,19 7,17.5 8,16.5C9.3,16.8 10.6,17 12,17C17.5,17 22,14 22,10.14C22,6.5 17.5,3.5 12,3.5M12,5.5C16.4,5.5 20,7.6 20,10.14C20,12.7 16.4,14.8 12,14.8C10.8,14.8 9.6,14.6 8.5,14.25L7.75,14L7.14,14.5C6.4,15.1 5.38,15.7 4.25,16.14C4.85,15.14 5.3,14 5.4,12.85L5.5,12.14L4.85,11.75C3.7,11.14 3,10.25 3,10.14C3,7.6 6.5,5.5 12,5.5Z"
              />
            </svg>
            Feedback abgeben
          </h3>

          <form
            class="space-y-4"
            @submit.prevent="submit"
          >
            <div>
              <label class="block text-[10px] font-bold uppercase tracking-wider opacity-60 mb-1.5">
                Art des Feedbacks
              </label>
              <div class="grid grid-cols-2 gap-2 sm:grid-cols-4">
                <button
                  v-for="option in options"
                  :key="option.value"
                  type="button"
                  class="py-2 text-xs font-semibold rounded-standard border transition-all duration-150 cursor-pointer"
                  :class="
                    category === option.value
                      ? 'bg-accent border-accent text-text-main shadow-sm'
                      : 'bg-primary border-tertiary text-text-main/70 hover:bg-primary/80'
                  "
                  @click="category = option.value"
                >
                  {{ option.text }}
                </button>
              </div>
              <span
                v-if="errors.category"
                class="text-red-500 text-[10px] mt-1"
              >{{ errors.category }}</span>
            </div>

            <div>
              <label class="block text-[10px] font-bold uppercase tracking-wider opacity-60 mb-1.5">
                Titel
              </label>
              <input
                v-model="title"
                type="text"
                placeholder="Kurze Zusammenfassung"
                class="w-full px-4 py-2 bg-primary border border-tertiary rounded-inner-popup text-sm text-text-main placeholder:text-text-main/30 focus:outline-none focus:ring-1 focus:ring-accent"
              >
              <span
                v-if="errors.title"
                class="text-red-500 text-[10px] mt-1"
              >{{ errors.title }}</span>
            </div>

            <div>
              <label class="block text-[10px] font-bold uppercase tracking-wider opacity-60 mb-1.5">
                Beschreibung
              </label>
              <textarea
                v-model="message"
                rows="4"
                placeholder="(Optional) Schreibe uns deine Anmerkungen oder beschreibe den Fehler..."
                class="w-full px-4 py-2 bg-primary border border-tertiary rounded-inner-popup text-sm text-text-main placeholder:text-text-main/30 focus:outline-none focus:ring-1 focus:ring-accent resize-none"
              />
            </div>

            <div class="flex items-center gap-2 pt-1">
              <input
                id="checkbox"
                v-model="checked"
                type="checkbox"
                :disabled="category === 'bug'"
                class="w-4 h-4 text-accent bg-primary border-tertiary rounded focus:ring-accent focus:ring-2 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
              <label 
                for="checkbox" 
                class="text-xs text-text-main cursor-pointer select-none"
                :class="{ 'opacity-50 cursor-not-allowed': category === 'bug' }"
              >
                Aktuellen Code aus dem Editor mitsenden
              </label>
            </div>

            <div class="flex items-center justify-end gap-2 pt-2">
              <button
                type="button"
                class="px-4 py-2 bg-primary border border-tertiary text-text-main text-xs font-semibold rounded-standard cursor-pointer transition-colors duration-150 hover:bg-primary/80"
                @click="isOpen = false"
              >
                Abbrechen
              </button>
              <button
                type="submit"
                :disabled="isSubmitting"
                class="px-5 py-2 bg-accent text-text-main text-xs font-semibold rounded-standard cursor-pointer transition-all duration-150 hover:brightness-110 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center min-w-22.5"
              >
                <span v-if="isSubmitting">Wird gesendet...</span>
                <span v-else>Absenden</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </Transition>
    <Transition name="fade">
      <div
        v-if="showErrorDialog"
        class="fixed inset-0 z-[10000] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
        @click.self="showErrorDialog = false"
      >
        <div class="w-full max-w-sm bg-secondary border border-tertiary p-6 rounded-outer-popup shadow-2xl text-left transform transition-all relative">
          <h3 class="text-accent text-lg font-medium">
            Leider ist etwas schief gelaufen!
          </h3>
          <p class="text-text-main text-base mt-1">
            versuche es erneut.
          </p>
          <div class="flex justify-end mt-8">
            <button
              class="bg-accent hover:brightness-90 text-white px-8 py-2 rounded-standard text-sm font-semibold transition-colors cursor-pointer"
              @click="showErrorDialog = false"
            >
              Schließen
            </button>
          </div>
        </div>
      </div>
    </Transition>

    <Transition name="fade">
      <div
        v-if="showSuccessDialog"
        class="fixed inset-0 z-[10000] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
        @click.self="closeAll"
      >
        <div class="w-full max-w-sm bg-secondary border border-tertiary p-6 rounded-outer-popup shadow-2xl text-left transform transition-all relative">
          <h3 class="text-green-500 text-lg font-medium">
            Feedback erfolgreich gesendet!
          </h3>
          <p class="text-text-main text-base mt-1">
            Vielen Dank für dein Feedback.
          </p>
          <div class="flex justify-end mt-8">
            <button
              class="bg-accent hover:brightness-90 text-text-main px-8 py-2 rounded-standard text-sm font-semibold transition-colors cursor-pointer"
              @click="closeAll"
            >
              Schließen
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>
